/**
 * TravelListPage — /travel/:type
 * Renders operators of a given travel type, grouped geographically
 * (country → state → city). Powered by GET /api/travel/geo/:type.
 */
import { useEffect, useMemo, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import axios from "axios";
import {
  Plane, Building2, Train, Bus, Ship, Anchor, Car, ArrowLeft, MapPin, Sparkles, Search, X,
} from "lucide-react";
import TravelCategoryNav from "@/components/TravelCategoryNav";

const API = process.env.REACT_APP_BACKEND_URL;

const TYPE_META = {
  airline:        { icon: Plane,      label: "Airlines",              plural: "Airlines" },
  airport:        { icon: Building2,  label: "Airports",              plural: "Airports" },
  rail_operator:  { icon: Train,      label: "Rail Operators",        plural: "Rail Operators" },
  rail_station:   { icon: Train,      label: "Rail Stations",         plural: "Rail Stations" },
  bus_terminal:   { icon: Bus,        label: "Bus Terminals",         plural: "Bus Terminals" },
  boat_terminal:  { icon: Ship,       label: "Boat & Ferry Terminals",plural: "Boat / Ferry Terminals" },
  seaport:        { icon: Anchor,     label: "Seaports",              plural: "Seaports" },
  taxi_service:   { icon: Car,        label: "Taxi & Rideshare",      plural: "Taxi & Rideshare Companies" },
};

// Slug → API type mapping (URL uses friendly slugs like /travel/airlines)
const SLUG_TO_TYPE = {
  airlines: "airline",
  airports: "airport",
  rail:     "rail_operator",
  "rail-stations": "rail_station",
  bus:      "bus_terminal",
  cruises:  "boat_terminal",
  ferries:  "boat_terminal",
  seaports: "seaport",
  ports:    "seaport",
  taxi:     "taxi_service",
};

export default function TravelListPage() {
  const { type: slugType } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const apiType = SLUG_TO_TYPE[slugType] || slugType;
  const meta = TYPE_META[apiType];
  const [data, setData] = useState(null);
  const [operators, setOperators] = useState(null); // list-view fallback
  const [q, setQ] = useState("");
  const [error, setError] = useState(null);

  const country = searchParams.get("country") || "";
  const state   = searchParams.get("state")   || "";
  const city    = searchParams.get("city")    || "";
  const hasFilter = !!(country || state || city);

  useEffect(() => {
    if (!apiType) return;
    setData(null); setOperators(null); setError(null);
    axios.get(`${API}/api/travel/geo/${apiType}`)
      .then(r => setData(r.data))
      .catch(e => setError(e?.response?.data?.detail || "Failed to load."));
    axios.get(`${API}/api/travel/operators`, { params: { type: apiType, limit: 200 } })
      .then(r => setOperators(r.data.items || []))
      .catch(() => {});
  }, [apiType]);

  // Filter the geo tree client-side based on query params for instant response.
  const filteredData = useMemo(() => {
    if (!data) return null;
    if (!hasFilter) return data;
    const norm = (s) => (s || "").toLowerCase().trim();
    return {
      ...data,
      countries: data.countries
        .filter(c => !country || norm(c.country) === norm(country))
        .map(c => ({
          ...c,
          groups: c.groups
            .filter(g => (!state || norm(g.state) === norm(state)) && (!city || norm(g.city) === norm(city))),
        }))
        .filter(c => c.groups.length > 0)
        .map(c => ({ ...c, count: c.groups.reduce((n, g) => n + g.items.length, 0) })),
    };
  }, [data, country, state, city, hasFilter]);

  const clearFilters = () => {
    const next = new URLSearchParams(searchParams);
    ["country", "state", "city"].forEach(k => next.delete(k));
    setSearchParams(next);
  };

  if (!meta) {
    return (
      <div className="max-w-3xl mx-auto px-4 py-16 text-center">
        <h1 className="font-display text-3xl mb-2">Unknown travel category</h1>
        <Link to="/travel" className="wofy-link">← Back to Travel home</Link>
      </div>
    );
  }

  const Icon = meta.icon;

  const filtered = (list) => {
    const needle = q.trim().toLowerCase();
    if (!needle) return list;
    return list.filter(o =>
      (o.name || "").toLowerCase().includes(needle) ||
      (o.code || "").toLowerCase().includes(needle) ||
      (o.city || "").toLowerCase().includes(needle)
    );
  };

  return (
    <div className="max-w-6xl mx-auto px-4 py-8" data-testid={`travel-list-${apiType}`}>
      <TravelCategoryNav />
      {/* Header */}
      <div className="mb-6 mt-4">
        <Link to="/travel" className="inline-flex items-center gap-1 text-sm opacity-70 hover:opacity-100" data-testid="back-to-travel">
          <ArrowLeft className="w-4 h-4" /> Travel
        </Link>
        <div className="mt-3 flex items-start gap-4 flex-wrap">
          <div className="w-14 h-14 rounded-xl grid place-items-center text-white" style={{ background: "var(--wofy-primary)" }}>
            <Icon className="w-7 h-7" />
          </div>
          <div className="min-w-0 flex-1">
            <div className="overline">SOCIAL PULSE TRAVEL</div>
            <h1 className="font-display text-4xl sm:text-5xl leading-tight">{meta.plural}</h1>
            <p className="opacity-70 text-sm mt-1">Browse by country → state → city. Every entry links to a detailed profile.</p>
          </div>
        </div>

        {/* Search */}
        <div className="mt-4 max-w-md relative">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 opacity-50" />
          <input
            type="text"
            value={q}
            onChange={e => setQ(e.target.value)}
            placeholder={`Search ${meta.plural.toLowerCase()}…`}
            className="w-full pl-9 pr-3 py-2 rounded-lg border text-sm focus:outline-none focus:ring-2"
            style={{ borderColor: "var(--wofy-border)" }}
            data-testid="travel-search-input"
          />
        </div>
      </div>

      {/* Active filter chip */}
      {hasFilter && (
        <div className="mb-4 flex items-center gap-2 flex-wrap" data-testid="active-filters">
          <span className="text-xs opacity-70">Filtered:</span>
          {[country, state, city].filter(Boolean).map(v => (
            <span key={v} className="inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs" style={{ background: "var(--wofy-primary)", color: "#fff" }}>
              <MapPin className="w-3 h-3" />{v}
            </span>
          ))}
          <button onClick={clearFilters} className="text-xs opacity-70 hover:opacity-100 inline-flex items-center gap-0.5" data-testid="clear-filters">
            <X className="w-3 h-3" />Clear
          </button>
        </div>
      )}

      {error && (
        <div className="wofy-card !p-4 mb-6 text-sm" style={{ background: "#FEE2E2", color: "#7F1D1D" }}>
          {error}
        </div>
      )}

      {/* Geographical browser */}
      {filteredData && filteredData.countries && filteredData.countries.length > 0 && (
        <div className="space-y-6">
          {filteredData.countries.map(c => (
            <section key={c.country} className="wofy-card !p-5" data-testid={`travel-country-${c.country.toLowerCase().replace(/\s+/g,'-')}`}>
              <div className="flex items-center gap-2 mb-3">
                <MapPin className="w-4 h-4 opacity-60" />
                <h2 className="font-display text-2xl">{c.country}</h2>
                <span className="text-xs opacity-60">({c.count})</span>
              </div>
              <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
                {c.groups.flatMap(g => filtered(g.items)).map(op => (
                  <Link
                    key={op.id}
                    to={`/travel/${slugType}/${op.slug}`}
                    className="block p-3 rounded-lg border hover:shadow-md transition"
                    style={{ borderColor: "var(--wofy-border)" }}
                    data-testid={`travel-op-${op.slug}`}
                  >
                    <div className="flex items-start gap-3">
                      {op.photo_url ? (
                        <img src={op.photo_url} alt="" className="w-12 h-12 rounded-md object-cover flex-shrink-0" />
                      ) : (
                        <div className="w-12 h-12 rounded-md grid place-items-center flex-shrink-0" style={{ background: "var(--wofy-surface)" }}>
                          <Icon className="w-5 h-5 opacity-60" />
                        </div>
                      )}
                      <div className="min-w-0 flex-1">
                        <div className="font-medium leading-tight truncate">{op.name}</div>
                        <div className="text-xs opacity-70 truncate">
                          {op.code && <span className="font-mono mr-1">{op.code}</span>}
                          {[op.city, op.state].filter(Boolean).join(", ")}
                        </div>
                      </div>
                      {op.article_slug && (
                        <span className="text-[9px] font-mono uppercase tracking-wider px-1.5 py-0.5 rounded-full flex-shrink-0" style={{ background: "#DBEAFE", color: "#1E3A8A" }}>
                          <Sparkles className="w-2.5 h-2.5 inline mr-0.5" />guide
                        </span>
                      )}
                    </div>
                  </Link>
                ))}
              </div>
            </section>
          ))}
        </div>
      )}

      {/* Empty state */}
      {filteredData && filteredData.countries.length === 0 && (
        <div className="wofy-card !p-10 text-center">
          <Icon className="w-10 h-10 mx-auto opacity-40 mb-3" />
          <h2 className="font-display text-2xl mb-1">
            {hasFilter ? `No ${meta.plural.toLowerCase()} match this filter` : `No ${meta.plural.toLowerCase()} yet`}
          </h2>
          <p className="opacity-70 text-sm mb-4">
            {hasFilter ? "Try clearing the filter or picking a different country." : "Be the first to add one."}
          </p>
          {hasFilter ? (
            <button onClick={clearFilters} className="wofy-btn-primary" data-testid="clear-filters-empty">Clear filter</button>
          ) : (
            <Link to="/travel/operators/new" className="wofy-btn-primary inline-block" data-testid="add-operator-cta">Add a {meta.label}</Link>
          )}
        </div>
      )}

      {/* Add CTA */}
      {filteredData && filteredData.countries.length > 0 && (
        <div className="mt-8 wofy-card !p-5 flex items-center justify-between flex-wrap gap-3">
          <div>
            <div className="font-display text-lg">Missing a listing?</div>
            <div className="text-sm opacity-70">Anyone signed-in can suggest a new {meta.label.toLowerCase()}.</div>
          </div>
          <Link to="/travel/operators/new" className="wofy-btn-primary inline-block text-sm" data-testid="add-operator-cta-bottom">Add {meta.label}</Link>
        </div>
      )}
    </div>
  );
}
